import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { change, getFormValues } from 'redux-form';
import SendIcon from 'mdi-react/SendIcon';
import PlaylistCheckIcon from 'mdi-react/PlaylistCheckIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import PlusCircleOutlineIcon from 'mdi-react/PlusCircleOutlineIcon';
import { sendLimitlessAiMessage } from '../../../../shared/services/limitlessAiApi';
import { limitlessAiRequestAction } from '../../../../redux/actions/limitlessAiActions';

const LimitlessAiChat = ({ displayName }) => {
  const dispatch = useDispatch();
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const limitlessAi = useSelector(store => store.limitlessAi);
  const {
    page, scope, scopeDescription, context, formName,
  } = limitlessAi;
  const currentFormValues = useSelector(state => (formName ? getFormValues(formName)(state) : null)) || {};

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Guided Setup ("interview") mode: walks the user through unfilled fields one at a time.
  const [interviewActive, setInterviewActive] = useState(false);
  const [interviewQueue, setInterviewQueue] = useState([]); // remaining fields still to be asked about
  const [currentInterviewField, setCurrentInterviewField] = useState(null); // field awaiting an answer
  const [interviewTotal, setInterviewTotal] = useState(0);
  const [interviewStep, setInterviewStep] = useState(0);

  const fieldSchema = Array.isArray(context?.fieldSchema) ? context.fieldSchema : [];
  const canStartInterview = !!formName && fieldSchema.length > 0 && context?.editModalOpen !== false;

  const isFieldFilled = (fieldName) => {
    const value = currentFormValues?.[fieldName];
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'object' && 'value' in value) {
      return value.value !== null && value.value !== undefined && value.value !== '';
    }
    return true;
  };

  // Required fields first, then optional; skip anything already filled in on the form
  const buildInterviewQueue = () => {
    const unfilled = fieldSchema.filter(field => !isFieldFilled(field.name));
    const required = unfilled.filter(field => field.required);
    const optional = unfilled.filter(field => !field.required);
    return [...required, ...optional];
  };

  const applyActions = (actions) => {
    if (!Array.isArray(actions) || !formName) return;
    actions.forEach((action) => {
      if (action.type === 'set_field' && action.fieldName) {
        dispatch(change(formName, action.fieldName, action.value));
      }
    });
  };

  // Renders the assistant's question for a given field (shown locally, no server call needed
  // since we already know the field's label/tooltip/type from the schema)
  const askAboutField = (field, position, total) => {
    const optionsHint = field.type === 'select' || field.type === 'multiselect' ? ' (Choose from the available options, or ask me what the options are.)' : '';
    const requiredHint = field.required ? ' *Required*' : ' (Optional - say "skip" to move on)';
    setMessages(prev => ([...prev, {
      role: 'assistant',
      content: `Step ${position}/${total}: **${field.label}**${requiredHint}\n${field.tooltip || ''}${optionsHint}`,
    }]));
  };

  const finishInterview = (endedEarly) => {
    setInterviewActive(false);
    setInterviewQueue([]);
    setCurrentInterviewField(null);
    setInterviewTotal(0);
    setInterviewStep(0);
    setMessages(prev => ([...prev, {
      role: 'assistant',
      content: endedEarly
        ? 'Guided Setup ended. You can resume anytime by clicking "Guided Setup" again.'
        : 'That covers every field! Review your entries and click Submit when you\'re ready.',
    }]));
  };

  const advanceInterview = (queue, stepSoFar) => {
    if (queue.length === 0) {
      setCurrentInterviewField(null);
      finishInterview(false);
      return;
    }
    const [nextField, ...rest] = queue;
    setInterviewQueue(rest);
    setCurrentInterviewField(nextField);
    setInterviewStep(stepSoFar + 1);
    askAboutField(nextField, stepSoFar + 1, interviewTotal);
  };

  const startInterview = () => {
    const queue = buildInterviewQueue();
    if (queue.length === 0) {
      setMessages(prev => ([...prev, { role: 'assistant', content: 'Every field on this form already has a value! You\'re ready to submit.' }]));
      return;
    }
    const [firstField, ...rest] = queue;
    setInterviewActive(true);
    setInterviewTotal(queue.length);
    setInterviewQueue(rest);
    setCurrentInterviewField(firstField);
    setInterviewStep(1);
    setMessages(prev => ([...prev, {
      role: 'assistant',
      content: `Let's do a Guided Setup! I'll walk you through ${queue.length} field${queue.length !== 1 ? 's' : ''} one at a time. You can say "skip" on any optional field, or click "End" above to stop early.`,
    }]));
    askAboutField(firstField, 1, queue.length);
  };

  const endInterview = () => finishInterview(true);

  const handleAddBudgetGroupClick = () => {
    dispatch(limitlessAiRequestAction('open_add_budget_group'));
    setMessages(prev => ([...prev, { role: 'assistant', content: 'Opening a new Budget Group for you now...' }]));
  };

  // Shown whenever the edit modal isn't open yet, since the real "Add Budget Group"/"Edit"
  // buttons on the page aren't visible while the Limitless AI tray covers that area.
  const renderOpenFormPrompt = () => (
    <div className="limitless-ai__quick-actions">
      <button
        type="button"
        className="limitless-ai__quick-action-btn"
        onClick={handleAddBudgetGroupClick}
      >
        <PlusCircleOutlineIcon /> Add Budget Group
      </button>
      <span className="limitless-ai__quick-actions-or">or click &quot;Edit&quot; on a budget group row</span>
    </div>
  );

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isSending) return;

    const nextMessages = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setInputValue('');

    // If this page has told us the relevant edit form isn't open (e.g. the Budget Group
    // Edit/Add modal), guide the user locally instead of round-tripping to the server.
    if (context && context.editModalOpen === false && context.editModalGuidance) {
      setMessages(prev => ([...prev, { role: 'assistant', content: context.editModalGuidance }]));
      return;
    }

    // During Guided Setup, let the user skip an optional field locally without a server call.
    if (interviewActive && currentInterviewField && !currentInterviewField.required && /^skip$/i.test(trimmed)) {
      setMessages(prev => ([...prev, { role: 'assistant', content: `Skipped "${currentInterviewField.label}".` }]));
      advanceInterview(interviewQueue, interviewStep);
      return;
    }

    setIsSending(true);

    const { reply, actions } = await sendLimitlessAiMessage({
      accountId: selectedSeller.value,
      retailer: context?.platform,
      page,
      scope,
      scopeDescription,
      context: {
        ...context,
        // Tell the server which single field we're currently collecting an answer for,
        // so it can interpret the reply in that context and return a matching set_field
        // action instead of trying to guess which field the user means.
        ...(interviewActive && currentInterviewField && {
          interviewMode: true,
          currentInterviewField: {
            name: currentInterviewField.name,
            label: currentInterviewField.label,
            type: currentInterviewField.type,
            required: currentInterviewField.required,
          },
        }),
      },
      formName,
      messages: nextMessages,
      jwtToken: user?.jwtToken,
    });

    setIsSending(false);

    if (reply) {
      setMessages(prev => ([...prev, { role: 'assistant', content: reply }]));
    }

    if (actions && actions.length > 0) {
      applyActions(actions);
      const actionSummary = actions
        .filter(action => action.type === 'set_field')
        .map(action => `Set "${action.fieldName}" to "${JSON.stringify(action.value)}"`)
        .join('; ');
      if (actionSummary) {
        setMessages(prev => ([...prev, { role: 'system', content: actionSummary }]));
      }
    }

    if (interviewActive) {
      advanceInterview(interviewQueue, interviewStep);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="limitless-ai__chat">
      {(interviewActive || fieldSchema.length > 0) && (
      <div className="limitless-ai__toolbar">
        {interviewActive ? (
          <>
            <div className="limitless-ai__interview-progress">
              <div className="limitless-ai__interview-progress-bar">
                <div
                  className="limitless-ai__interview-progress-fill"
                  style={{ width: `${Math.round((interviewStep / (interviewTotal || 1)) * 100)}%` }}
                />
              </div>
              <span className="limitless-ai__interview-progress-label">
                Guided Setup: {interviewStep}/{interviewTotal}
              </span>
            </div>
            <button
              type="button"
              className="limitless-ai__interview-end-btn"
              onClick={endInterview}
              aria-label="End Guided Setup"
              title="End Guided Setup"
            >
              <CloseIcon /> End
            </button>
          </>
        ) : (
          fieldSchema.length > 0 && (
            <button
              type="button"
              className="limitless-ai__interview-start-btn"
              onClick={startInterview}
              disabled={!canStartInterview}
              title={canStartInterview ? 'Have Limitless AI walk you through this form field by field' : 'Open the budget form to start a Guided Setup'}
            >
              <PlaylistCheckIcon /> Guided Setup
            </button>
          )
        )}
      </div>
      )}
      <div className="limitless-ai__messages">
        {messages.length === 0 && (
          <p className="limitless-ai__empty-state">
            {(() => {
              if (context && context.editModalOpen === false && context.editModalGuidance) {
                return context.editModalGuidance;
              }
              if (canStartInterview) {
                return `Ask ${displayName} about this form, or click "Guided Setup" above to be walked through it field by field.`;
              }
              return `Ask ${displayName} anything about this page and I'll do my best to help.`;
            })()}
          </p>
        )}
        {messages.map((message, index) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`limitless-ai-message-${index}`}
            className={`limitless-ai__message limitless-ai__message--${message.role}`}
          >
            {message.content}
          </div>
        ))}
        {isSending && (
          <div className="limitless-ai__message limitless-ai__message--assistant limitless-ai__message--pending">
            {displayName} is thinking...
          </div>
        )}
        {context && context.editModalOpen === false && renderOpenFormPrompt()}
      </div>
      <div className="limitless-ai__input-row">
        <textarea
          className="limitless-ai__input"
          value={inputValue}
          placeholder={interviewActive && currentInterviewField
            ? `Your answer for "${currentInterviewField.label}"...`
            : `Ask ${displayName} about this form...`}
          onChange={event => setInputValue(event.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
        />
        <button
          type="button"
          className="limitless-ai__send-btn"
          onClick={handleSend}
          disabled={isSending || !inputValue.trim()}
          aria-label="Send"
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
};

LimitlessAiChat.propTypes = {
  displayName: PropTypes.string.isRequired,
};

export default LimitlessAiChat;
